<template>
    <v-dialog v-model="showDialog" max-width="600px">
        <v-card>
            <v-card-title>
                {{ user ? "Editar usuario" : "Nuevo usuario" }}
            </v-card-title>

            <v-card-text>
                <v-form ref="form" v-model="valid" validate-on="input">
                    <v-text-field
                        v-model="userForm.name"
                        label="Nombre"
                        variant="outlined"
                        density="compact"
                        required
                        :rules="[(v: string) => !!v || 'El nombre es obligatorio']"
                    />
                    <v-text-field
                        v-model="userForm.username"
                        label="Username"
                        variant="outlined"
                        density="compact"
                        required
                        :rules="[(v: string) => !!v || 'El username es obligatorio']"
                    />

                    <v-checkbox
                        v-model="userForm.isAdmin"
                        label="Es administrador"
                        density="compact"
                        hide-details
                    />
                    <v-checkbox
                        v-model="userForm.disabled"
                        label="Deshabilitado"
                        density="compact"
                    />

                    <v-divider class="mb-4"></v-divider>

                    <div>
                        <h4 class="mb-2">
                            Contraseña
                        </h4>

                        <p v-if="isUpdating" class="mb-2 text-caption font-weight-light">
                            Si no desea cambiar la contraseña, deje los campos vacíos.
                        </p>

                        <p v-if="isCreating" class="mb-2 text-caption font-weight-light">
                            La contraseña es obligatoria.
                        </p>

                        <v-text-field
                            v-model="userForm.password"
                            label="Contraseña"
                            variant="outlined"
                            density="compact"
                            type="password"
                            :rules="passwordRules"
                        />
                        <v-text-field
                            v-model="userForm.confirmPassword"
                            label="Confirmar contraseña"
                            variant="outlined"
                            density="compact"
                            type="password"
                            :rules="confirmPasswordRules"
                        />
                    </div>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer/>
                <v-btn @click="cancel">
                    Cancelar
                </v-btn>
                <v-btn @click="save" color="primary" variant="tonal" :disabled="!valid">
                    {{ user ? "Actualizar" : "Crear" }}
                </v-btn>
            </v-card-actions>
        </v-card>

    </v-dialog>
</template>

<script lang="ts" setup>
import { AuthService, CreateUserForm, SystemUser, UpdateUser } from '@/services/auth/authService';
import { PropType, defineProps, ref, watch, defineEmits, defineModel, computed } from 'vue';
import { useToast } from 'vue-toast-notification';

const props = defineProps({
    user: {
        type: Object as PropType<SystemUser>,
        required: false
    },
});

const authService = new AuthService();
const toast = useToast();
const showDialog = defineModel<boolean>({default: false});
const valid = ref(false);

const userForm = ref<UpdateUser>({
    id: props.user?.id,
    name: props.user?.name || "",
    username: props.user?.username || "",
    isAdmin: props.user?.isAdmin || false,
    disabled: props.user?.disabled || false,
});

const emit = defineEmits<{
    (event: "update:user", value: SystemUser): void;
    (event: "create:user", value: SystemUser): void;
    (event: "cancel"): void;
}>();

const isUpdating = computed(() => {
    return !!props.user;
})

const isCreating = computed(() => {
    return !props.user;
})

const passwordRules = computed(() => {
    if (isCreating.value) {
        return [
            (v: string) => !!v || 'La contraseña es obligatoria',
            (v: string) => v.length >= 8 || 'La contraseña debe tener al menos 8 caracteres',
        ]
    }
    return []
})

const confirmPasswordRules = computed(() => {
    if (isCreating.value) {
        return [
            (v: string) => !!v || 'La contraseña es obligatoria',
            (v: string) => v === userForm.value.password || 'Las contraseñas no coinciden',
        ]
    }
    else if (isUpdating.value && userForm.value.password) {
        return [
            (v: string) => !!v || 'La contraseña es obligatoria',
            (v: string) => v === userForm.value.password || 'Las contraseñas no coinciden',
        ]
    }
    return []
})

async function save() {
    if (props.user) {
        const updateForm: UpdateUser = {
            id: userForm.value.id,
            name: userForm.value.name,
            username: userForm.value.username,
            isAdmin: userForm.value.isAdmin,
            disabled: userForm.value.disabled,
            password: userForm.value.password,
            confirmPassword: userForm.value.confirmPassword,
        }

        const response = await authService.updateUser(updateForm)

        if (response) {
            emit("update:user", response);
            showDialog.value = false;
            toast.success("Usuario actualizado correctamente", {
                position: "top-right",
                duration: 3000,
            });
            return
        }
    }


    else {
        if (!userForm.value.password || !userForm.value.confirmPassword) {
            return;
        }

        const createForm: CreateUserForm = {
            name: userForm.value.name,
            username: userForm.value.username,
            isAdmin: userForm.value.isAdmin,
            disabled: userForm.value.disabled,
            password: userForm.value.password,
            confirmPassword: userForm.value.confirmPassword,
        }

        const response = await authService.createUser(createForm)

        if (response) {
            emit("create:user", response);
            showDialog.value = false;
            toast.success("Usuario creado correctamente", {
                position: "top-right",
                duration: 3000,
            });
            return
        }
    }

    toast.error("Error al guardar el usuario", {
        position: "top-right",
        duration: 3000,
    });
}

function cancel() {
    showDialog.value = false;
}

watch(showDialog, (newValue) => {
    if (newValue) {
        userForm.value = {
            id: props.user?.id,
            name: props.user?.name || "",
            username: props.user?.username || "",
            isAdmin: props.user?.isAdmin || false,
            disabled: props.user?.disabled || false,
        };
    }
});
</script>

<style lang="scss" scoped>

</style>